import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import type { z } from 'zod';
import { makeDomainQueryOptions } from '#api/domains.queries.ts';
import { useAddEndpointOIDCProvider, useEditEndpointOIDCProvider } from '#api/endpoint-oidc-providers.queries.ts';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { maxNetworksQueryOptions, networksQueryOptions } from '#api/networks.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { Checkbox } from '#components/atoms/forms/checkbox/Checkbox.tsx';
import { ErrorMessage } from '#components/atoms/forms/error/ErrorMessage.tsx';
import { Input, Label } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { SelectNetwork } from '#components/patterns/entity-select/network/SelectNetwork.tsx';
import { NO_ROLE_FLAG } from '#components/patterns/entity-select/role/option-constants.ts';
import { SelectRole } from '#components/patterns/entity-select/role/SelectRole.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import type { Network } from '#dn-types/network.ts';
import { useGetProvidersForDomain } from '#screens/settings/sso/domain-verification/useGetProvidersForDomain.ts';
import { useShowEnableBannerForProvider } from '#screens/settings/sso/list/useShowEnableBanner.ts';
import { convertHoursToFriendlyUnits } from '#screens/settings/sso/util/convertHoursToFriendlyUnits.ts';
import { getCidrsExceptV4, getCidrV4, getCidrV6 } from '#util/networkCidrs.ts';
import { setValueAsNumberOrNull } from '#util/validation/set-value-as-number-or-null.ts';
import { NEW_DOMAIN_FLAG, NO_DOMAIN_FLAG } from '../../SelectDomain.queries.ts';
import { SelectDomain } from '../../SelectDomain.tsx';
import { useCreatableDomain } from '../hooks/useCreatableDomain.ts';
import { IdentityProviderBaseForm } from '../IdentityProviderBaseForm.tsx';
import { convertToHours } from './convertToHours.ts';
import {
  addEndpointIdentityProviderFormSchema,
  editEndpointIdentityProviderFormSchema,
  LIFETIME_FOREVER_UNIT,
  LIFETIME_UNITS,
  type AddEndpointIdentityProviderFormData,
  type EditEndpointIdentityProviderFormData,
} from './EndpointIdentityProviderForm.schema.ts';

type EditProps = {
  endpointIdentityProviderId: string;
  providerName: string;
  defaultDomainId: null;
  defaultValues: EndpointOIDC;
  onCancel: () => void;
  setGlobalErrorMsg: (message: string | null) => void;
  setNameErrorMsg: (message: string | null) => void;
};

type AddProps = {
  endpointIdentityProviderId: null;
  providerName: string;
  /** This will be specified when creating a provider from an unused domain */
  defaultDomainId?: string;
  onCancel: () => void;
  setGlobalErrorMsg: (message: string | null) => void;
  setNameErrorMsg: (message: string | null) => void;
};

const DEFAULT_VALUES = {
  name: '',
  clientID: '',
  clientSecret: '',
  issuerURL: '',
  domainID: '',
  domainName: '',
  networkID: '',
  roleID: '',
  certLifetimeUnit: 'Hours',
  certLifetime: 16,
  // Matches host, relay, and lighthouse creation, which all default to assigning an IPv4 address
  autoAssignIPv4: true,
} satisfies AddEndpointIdentityProviderFormData;

/**
 * Assigning IPv4 is only a choice on networks with an IPv6 CIDR; an IPv4-only network assigns one
 * to every host regardless.
 */
function isAutoAssignIPv4Choosable(network: Network | undefined) {
  return network ? Boolean(getCidrV6(network)) : false;
}

export function EndpointIdentityProviderForm(props: EditProps | AddProps) {
  const { endpointIdentityProviderId, setGlobalErrorMsg, defaultDomainId, setNameErrorMsg } = props;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const addOIDCProviderMutation = useAddEndpointOIDCProvider();
  const editOIDCProviderMutation = useEditEndpointOIDCProvider();
  const getProvidersForDomain = useGetProvidersForDomain();
  const { setProviderId: setProviderIdToPromptForEnable } = useShowEnableBannerForProvider();

  const {
    data: {
      data: networks,
      metadata: { totalCount: totalNetworksCount },
    },
  } = useSuspenseQuery(networksQueryOptions);

  // Shares a cache entry with the network select, so this doesn't cost an extra fetch
  const {
    data: { data: allNetworks },
  } = useSuspenseQuery(maxNetworksQueryOptions);

  const defaultValues =
    endpointIdentityProviderId ?
      ({
        name: props.defaultValues.name,
        clientID: props.defaultValues.clientID,
        clientSecret: '',
        issuerURL: props.defaultValues.issuerURL,
        domainID: props.defaultValues.domainID ?? NO_DOMAIN_FLAG,
        domainName: '',
        networkID: props.defaultValues.networkID,
        roleID: props.defaultValues.roleID ?? NO_ROLE_FLAG,
        certLifetimeUnit: convertHoursToFriendlyUnits(props.defaultValues.authValidityHours)[1],
        certLifetime: convertHoursToFriendlyUnits(props.defaultValues.authValidityHours)[0],
        // An IPv4 prefix in the stored list is what opts endpoints into an IPv4 address
        autoAssignIPv4: Boolean(getCidrV4({ cidrs: props.defaultValues.autoAssignCIDRs ?? [] })),
      } satisfies EditEndpointIdentityProviderFormData)
    : {
        ...DEFAULT_VALUES,
        networkID: totalNetworksCount === 1 ? assert(networks[0]).id : '',
        domainID: defaultDomainId ?? '',
      };
  const [prevLifetime, setPrevLifetime] = useState<number>(defaultValues.certLifetime ?? 16);
  const isEditing = endpointIdentityProviderId !== null;
  const schema = isEditing ? editEndpointIdentityProviderFormSchema : addEndpointIdentityProviderFormSchema;
  type TransformedFormData = z.output<typeof schema>;

  const formContext = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting, isSubmitted, isDirty, isValid, dirtyFields },
  } = formContext;
  const lens = useLens({ control });
  const { createOrUseExistingDomain, hasCreatedDomain } = useCreatableDomain(formContext);

  // Handle editing of immutable fields (fields that only apply to newly-created hosts)
  const [dataToSubmitAfterConfirming, setDataToSubmitAfterConfirming] = useState<TransformedFormData>();
  const [isFinishingSubmission, setIsFinishingSubmission] = useState(false);
  const isImmutableFieldDirty = Boolean(dirtyFields.networkID) || Boolean(dirtyFields.roleID);

  // We show the name input in the wrapper, so we need to synchronize the form state and error state
  useEffect(() => {
    setValue('name', props.providerName, { shouldDirty: true });
  }, [setValue, props.providerName]);
  useEffect(() => {
    setNameErrorMsg(errors.name?.message ?? null);
  }, [errors.name?.message, setNameErrorMsg]);

  function convertFormForServer(data: TransformedFormData, newDomainId: string | null) {
    const submittedNetwork = allNetworks.find((n) => n.id === data.networkID);
    // Only send the prefix where assigning IPv4 is an actual choice. On an IPv4-only network the API
    // records its CIDR itself, since every host there gets an IPv4 address regardless, and sending a
    // cached copy would only add a way to 400 on a stale value the user never chose.
    // The trailing submittedNetwork check looks redundant but is what narrows it for getCidrV4;
    // isAutoAssignIPv4Choosable returns boolean, not a type guard.
    const autoAssignIPv4Cidr =
      data.autoAssignIPv4 && isAutoAssignIPv4Choosable(submittedNetwork) && submittedNetwork ?
        getCidrV4(submittedNetwork)
      : undefined;

    // The API validates every prefix against the request's networkID, so a list it handed us stays
    // valid only while the network is unchanged. Round-trip it untouched when the user touched
    // neither the checkbox nor the network, rather than flattening shapes this checkbox cannot
    // express — a pool narrower than the network, or more than one prefix per family.
    //
    // A rebuild still carries forward the entries the checkbox has no say over: toggling IPv4 must
    // not rewrite IPv6 policy. Changing the network drops them, since they describe the old one.
    const preservedCIDRs =
      isEditing && !dirtyFields.networkID ? getCidrsExceptV4({ cidrs: props.defaultValues.autoAssignCIDRs ?? [] }) : [];
    const rebuiltCIDRs = autoAssignIPv4Cidr ? [autoAssignIPv4Cidr, ...preservedCIDRs] : preservedCIDRs;

    // The API reads the presence of an IPv4 prefix as the signal to assign one; null leaves
    // auto-assignment unset.
    const autoAssignCIDRs =
      isEditing && !dirtyFields.autoAssignIPv4 && !dirtyFields.networkID ? props.defaultValues.autoAssignCIDRs
      : rebuiltCIDRs.length > 0 ? rebuiltCIDRs
      : null;

    return {
      name: data.name,
      // Default to disabled for new providers (domain will need to be verified)
      enabled: isEditing ? props.defaultValues.enabled : false,
      clientID: data.clientID,
      // We need to send a `null` to signal to not change the client secret. Sending an empty string will error.
      clientSecret: data.clientSecret.length > 0 ? data.clientSecret : null,
      issuerURL: data.issuerURL,
      networkID: data.networkID,
      roleID: data.roleID === NO_ROLE_FLAG ? null : data.roleID,
      domainID:
        // turn NO_DOMAIN_FLAG to null
        data.domainID === NO_DOMAIN_FLAG ? null
          // If creating a new domain, use its ID
        : data.domainID === NEW_DOMAIN_FLAG && newDomainId ? newDomainId
        : data.domainID,
      authValidityHours: convertToHours(data.certLifetimeUnit, data.certLifetime),
      autoAssignCIDRs,
    };
  }

  // After successful submissions, we may need to go to the domain verification page, or show an enable banner
  function makeHandleSuccess(shouldVerifyDomain: boolean) {
    return async function handleSuccess(data: EndpointOIDC) {
      if (shouldVerifyDomain && data.domainID) {
        void navigate(`/settings/sso/domains/${data.domainID}/verify`);
        return;
      }

      // If this is the only provider using the domain, we want to show a banner prompting the user to enable the provider
      if (data.domainID && (await getProvidersForDomain(data.domainID)).length === 1) {
        setProviderIdToPromptForEnable(data.id);
      }

      void navigate('/settings/sso?tab=endpoint');
    };
  }

  /**
   * Submission does two things:
   *
   * 1) Creates a new domain if necessary
   * 2) Creates/edits an endpoint oidc provider, using either the new domain or a provided domainId
   *
   * Note, there's a possible intermediate state, where the domain creation succeeded,
   * but provider creation failed (duplicate name, for example).  In this case, we'll use
   * the ID from the previously-created new domain.
   */
  const onSubmit = handleSubmit(async (data) => {
    setGlobalErrorMsg(null);

    if (isEditing && isImmutableFieldDirty) {
      setDataToSubmitAfterConfirming(data);
    } else {
      await finishSubmitting(data);
    }
  });

  async function finishSubmitting(data: TransformedFormData) {
    setDataToSubmitAfterConfirming(undefined);
    setIsFinishingSubmission(true);
    const domainID = await createOrUseExistingDomain(data);

    // Only try to create a provider if we didn't blow up when creating the domain
    if (typeof domainID === 'undefined') {
      return;
    }

    let shouldVerifyDomain = false;
    if (domainID) {
      const { data: domain } = await queryClient.fetchQuery(makeDomainQueryOptions(domainID));
      if (!domain.isVerified) {
        shouldVerifyDomain = true;
      }
    }

    try {
      if (isEditing) {
        await editOIDCProviderMutation.mutateAsync(
          {
            id: endpointIdentityProviderId,
            values: convertFormForServer(data, domainID),
          },
          {
            onSuccess: (provider) => {
              void makeHandleSuccess(shouldVerifyDomain)(provider);
            },
          }
        );
      } else {
        await addOIDCProviderMutation.mutateAsync(
          {
            ...convertFormForServer(data, domainID),
            // When adding a new provider, we can't supply a `null` client secret
            clientSecret: data.clientSecret,
          },
          {
            onSuccess: (provider) => {
              void makeHandleSuccess(shouldVerifyDomain)(provider);
            },
          }
        );
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        handleServerFormError(err, (error) => {
          const { path, message } = error;

          // The API reports the position within the CIDR list (`autoAssignCIDRs[0]`), but the form
          // exposes the list as a single checkbox, so any entry's error belongs to it.
          if (path && /^autoAssignCIDRs(\[\d+\])?$/.test(path)) {
            setError('autoAssignIPv4', { message }, { shouldFocus: true });
            return true;
          }

          switch (path) {
            case 'name':
            case 'clientID':
            case 'clientSecret':
            case 'issuerURL':
            case 'domainID':
            case 'networkID':
            case 'roleID':
            case 'certLifetime':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case undefined:
              setGlobalErrorMsg(message);
              return true;
            default:
              setGlobalErrorMsg('We couldn’t save your changes, please try again');
              return false;
          }
        });
      } else {
        trackError(err);
        setGlobalErrorMsg('We couldn’t save your changes, please try again');
      }
      setIsFinishingSubmission(false);
    }
  }

  const [certLifetimeUnitValue, certLifetimeValue, domainIDValue, networkIDValue, autoAssignIPv4Value] = useWatch({
    control,
    name: ['certLifetimeUnit', 'certLifetime', 'domainID', 'networkID', 'autoAssignIPv4'],
  });

  // IPv4 is assigned unconditionally on IPv4-only networks, so the option only means something on dual-stack ones.
  const selectedNetwork = allNetworks.find((n) => n.id === networkIDValue);
  const showAutoAssignIPv4 = isAutoAssignIPv4Choosable(selectedNetwork);
  const canAutoAssignIPv4 = selectedNetwork ? Boolean(getCidrV4(selectedNetwork)) : false;

  // Switching to a network without an IPv4 CIDR would make the saved value un-assignable, and the API rejects it.
  useEffect(() => {
    if (showAutoAssignIPv4 && !canAutoAssignIPv4 && autoAssignIPv4Value) {
      setValue('autoAssignIPv4', false, { shouldDirty: true });
    }
  }, [showAutoAssignIPv4, canAutoAssignIPv4, autoAssignIPv4Value, setValue]);

  return (
    <form action="POST" onSubmit={onSubmit} id="endpoint-provider-form">
      <Spacer direction="vertical" size="wide" className="mb-40">
        {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
        <Input className="width-100" hasError={Boolean(errors.name)} {...register('name')} hidden={true} />
        <IdentityProviderBaseForm
          className="mb-32"
          isEditing={isEditing}
          lens={lens.reflect((fields) => ({
            clientID: fields.clientID,
            clientSecret: fields.clientSecret,
            issuerURL: fields.issuerURL,
          }))}
        />
        <Divider />
        <h2 className="h4 mb-8">Access configuration</h2>
        <p className="p-md">
          Define how this provider integrates with your network - including its assigned domain, default role,
          certificate settings, and target network.
        </p>
        <SelectDomain
          name="domainID"
          label="Domain"
          control={control}
          disabled={hasCreatedDomain}
          rules={{ deps: 'domainName' }}
          errorMessage={errors.domainID?.message}
          helpTip="Endpoint users will provide their email address to log in. Those with this domain will use this provider.  You will need to verify that you own this domain before this provider can be enabled."
        />
        {domainIDValue === NEW_DOMAIN_FLAG ?
          <Field label="New domain">
            <Input
              className="width-100"
              hasError={Boolean(errors.domainName)}
              {...register('domainName', { disabled: hasCreatedDomain })}
            />
            {errors.domainName?.message && <ErrorMessage className="mb-8">{errors.domainName.message}</ErrorMessage>}
          </Field>
        : null}
        <Spacer size="wide" stackWhenNarrow={true}>
          <SelectNetwork
            label="Network"
            name="networkID"
            control={control}
            className="width-100"
            helpTip="Endpoints will be enrolled into this network."
            errorMessage={errors.networkID?.message}
          />
          <SelectRole
            label="Role"
            name="roleID"
            control={control}
            className="width-100"
            helpTip="Endpoints will be assigned this role when they are created.  It cannot be changed per-device."
            errorMessage={errors.roleID?.message}
          />
        </Spacer>
        {showAutoAssignIPv4 ?
          <Field>
            <Checkbox
              {...register('autoAssignIPv4')}
              // Not `register`'s `disabled`, which would submit `undefined` and fail schema validation
              disabled={!canAutoAssignIPv4}
              hasError={Boolean(errors.autoAssignIPv4)}
              label="Also assign an IPv4 address to enrolled endpoints"
            />
            {!canAutoAssignIPv4 && <p className="p-sm mb-0">This network has no IPv4 CIDR to assign addresses from.</p>}
            {errors.autoAssignIPv4?.message && (
              <ErrorMessage className="mb-8">{errors.autoAssignIPv4.message}</ErrorMessage>
            )}
          </Field>
        : null}
        <Spacer size="wide" stackWhenNarrow={true}>
          <SelectControlled
            label="Authentication validity"
            name="certLifetimeUnit"
            control={control}
            rules={{ deps: 'certLifetime' }}
            className="width-100"
            helpTip="After this time, endpoint users will lose connection to your network and will need to re-authenticate."
            renderOptions={({ onChange }) => (
              <>
                {LIFETIME_UNITS.map((u) => (
                  <SelectItem
                    key={u}
                    value={u}
                    onClick={() => {
                      if (u === LIFETIME_FOREVER_UNIT) {
                        setPrevLifetime(certLifetimeValue ?? 1);
                        setValue('certLifetime', null);
                      } else {
                        setValue('certLifetime', prevLifetime);
                      }
                      onChange(u);
                    }}
                  >
                    {u}
                  </SelectItem>
                ))}
              </>
            )}
          />
          <Field
            id="duration"
            label={
              // Align height with "Certificate lifetime", which includes a HelpTip
              <Label htmlFor="duration" style={{ height: 20 }}>
                Duration
              </Label>
            }
            className="width-100"
          >
            <Input
              {...register('certLifetime', {
                setValueAs: setValueAsNumberOrNull,
                disabled: certLifetimeUnitValue === LIFETIME_FOREVER_UNIT,
                onChange: (e) => {
                  if (e.target instanceof HTMLInputElement) {
                    setPrevLifetime(Number(e.target.value));
                  }
                },
              })}
              className="width-100"
              min={1}
              hasError={Boolean(errors.certLifetime)}
              type="number"
              inputMode="numeric"
              maxLength={5}
            />

            {errors.certLifetime?.message && (
              <ErrorMessage className="mb-8">{errors.certLifetime.message}</ErrorMessage>
            )}
          </Field>
        </Spacer>
      </Spacer>
      <ConfirmChangesBanner
        isOpen={isDirty}
        title={isSubmitted && !isValid ? 'Unable to save.  Please update and try again.' : undefined}
        primaryButton={
          <Button
            type="submit"
            disabled={isSubmitting || !isDirty || isFinishingSubmission || Boolean(dataToSubmitAfterConfirming)}
          >
            Save
          </Button>
        }
        secondaryButton={
          <Button
            onClick={() => {
              props.onCancel();
            }}
            variant="link"
            disabled={isSubmitting || isFinishingSubmission || Boolean(dataToSubmitAfterConfirming)}
          >
            Cancel
          </Button>
        }
      />
      {dataToSubmitAfterConfirming ?
        <ConfirmDialog
          title="Some changes apply only to new hosts"
          onCancel={() => {
            setDataToSubmitAfterConfirming(undefined);
          }}
          onAccept={() => {
            void finishSubmitting(dataToSubmitAfterConfirming);
          }}
          acceptButtonText="Save"
        >
          <p className="p">
            It is not possible to change the network or role of devices which have already been created. After saving,
            you can manually delete any such hosts and instruct owners to enroll their device again.
          </p>
          <p className="p mb-0">Proceed?</p>
        </ConfirmDialog>
      : null}
    </form>
  );
}
